import { z } from 'zod'
import { LOCALES } from '../constants'
import { ERROR_CODES } from '../errors'
import { normalizeBdPhone } from '../phone'

/** Shared primitives. Every API boundary parses through these. */

export const uuidSchema = z.string().regex(/^[0-9a-f-]{36}$/i, 'must be a UUID')
export const moneySchema = z.number().int().min(0)
export const localeSchema = z.enum(LOCALES)
export const timestampSchema = z.number().int().positive()

/**
 * Bangladeshi mobile number, accepted in any reasonable spelling (spaces,
 * dashes, +880/880/0 prefixes, Bengali numerals) and normalized to E.164 —
 * so one person is always one customer (FR-CRM-1).
 */
export const phoneSchema = z
  .string()
  .transform((value, ctx) => {
    const normalized = normalizeBdPhone(value)
    if (!normalized) {
      ctx.addIssue({ code: 'custom', message: 'must be a valid Bangladeshi mobile number' })
      return z.NEVER
    }
    return normalized
  })
  .transform((value) => `0${value.replace(/^(?:\+?880|0)/, '')}`)

export const localizedTextSchema = z.object({
  en: z.string().min(1),
  bn: z.string().min(1),
})
export type LocalizedText = z.infer<typeof localizedTextSchema>

export const paginationSchema = z.object({
  limit: z.coerce.number().int().min(1).max(100).default(25),
  cursor: z.string().optional(),
})

export const errorSchema = z.object({
  code: z.enum(ERROR_CODES),
  message: z.string(),
  details: z.unknown().optional(),
})

/** SRS §5 — every response is `{ ok, data }` or `{ ok, error }`. */
export function successSchema<T extends z.ZodTypeAny>(data: T) {
  return z.object({ ok: z.literal(true), data })
}

export const failureSchema = z.object({ ok: z.literal(false), error: errorSchema })

export type ApiSuccess<T> = { ok: true; data: T }
export type ApiFailure = z.infer<typeof failureSchema>
export type ApiResponse<T> = ApiSuccess<T> | ApiFailure

export function pickLocalized(text: LocalizedText, locale: 'en' | 'bn'): string {
  return text[locale] || text.en
}
